import { useCallback, useEffect, useRef, useState } from 'react';
import { getKioskSelectionFeedRequest } from 'api/requests/kiosk-session-requests';
import { fromResponseToKioskSelectionFeed } from 'mappers/kiosk-selection-feed-mapper';
import kioskSessionService from 'services/kiosk-session-service';
import { useAppDispatch } from 'store';
import { setKioskSessionAction, setKioskStatusAction } from 'store/actions/kiosk-session-actions';
import { KioskStatus } from 'types/kiosk/KioskStatus';
import { TKioskSelectionFeed } from 'types/kiosk/TKioskSelectionFeed';

/** A dedicated kiosk tab is always foregrounded, so focus/visibility events rarely fire — poll too. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Owns the selection feed: fetch on mount, re-fetch on window focus / tab becoming visible / a fixed
 * poll interval, and on manual reload. The poll interval matters most: an unattended kiosk tab never
 * loses focus/visibility, so without it the feed (and any time-window-dependent selection within it)
 * can go stale for hours.
 * On 401 the kiosk session is dead → clear it and flip to INACTIVE (router falls back to the activation gate).
 * On any other error the feed is cleared so stale sections are never shown (acceptance criterion).
 * Return type is inferred: { feed, loading, error, reload }.
 */
export const useKioskSelectionFeed = () => {
  const dispatch = useAppDispatch();
  const [feed, setFeed] = useState<TKioskSelectionFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const isMounted = useRef(true);

  const endSession = useCallback(() => {
    kioskSessionService.clear();
    dispatch(setKioskSessionAction(null));
    dispatch(setKioskStatusAction(KioskStatus.INACTIVE));
  }, [dispatch]);

  const fetchFeed = useCallback(async () => {
    const token = kioskSessionService.getToken();
    if (!token) {
      endSession();
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const data = await getKioskSelectionFeedRequest(token);
      if (!isMounted.current) return;
      setFeed(fromResponseToKioskSelectionFeed(data));
    } catch (e) {
      if (!isMounted.current) return;
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        endSession();
        return;
      }
      setError(true);
      setFeed(null);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [endSession]);

  useEffect(() => {
    isMounted.current = true;
    fetchFeed();

    const onFocus = () => {
      fetchFeed();
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchFeed();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const pollId = window.setInterval(fetchFeed, POLL_INTERVAL_MS);

    return () => {
      isMounted.current = false;
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(pollId);
    };
  }, [fetchFeed]);

  return { feed, loading, error, reload: fetchFeed };
};
